import { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, CheckCircle2, X, Reply } from "lucide-react";
import { toast } from "sonner";

const STATES = ["Open", "Answered", "Closed", "Cancelled"];

export default function Queries() {
  const [state, setState] = useState("Open");
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");

  const load = useCallback(async () => setList((await api.get(`/ecrf/queries${state ? `?state=${state}` : ""}`)).data), [state]);
  useEffect(() => { load(); }, [load]);

  const doReply = async () => {
    if (!reply.trim()) return toast.error("Response required");
    await api.post(`/ecrf/queries/${detail.query_id}/reply`, { query_id: detail.query_id, response: reply });
    toast.success("Query answered");
    setReply(""); await refreshDetail(); load();
  };
  const doClose = async () => {
    await api.post(`/ecrf/queries/${detail.query_id}/close`);
    toast.success("Query closed");
    await refreshDetail(); load();
  };
  const refreshDetail = async () => {
    const r = await api.get(`/ecrf/queries?entry_id=${detail.entry_id}`);
    const fresh = r.data.find((q) => q.query_id === detail.query_id);
    setDetail(fresh || null);
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="vq-overline mb-2">Clinical / eCRF</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">Query Workbench</h1>
            <p className="text-slate-600 mt-2">Discrepancies raised automatically by the constraint engine and by monitors during SDV.</p>
          </div>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="w-40 h-11" data-testid="q-state-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Open">Open</SelectItem>
              <SelectItem value="Answered">Answered</SelectItem>
              <SelectItem value="Closed">Closed</SelectItem>
              <SelectItem value="Cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 vq-card overflow-hidden">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 vq-overline">{state} ({list.length})</div>
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {list.map((q) => (
                <button key={q.query_id} data-testid={`q-row-${q.query_id}`}
                        onClick={() => setDetail(q)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${detail?.query_id === q.query_id ? "bg-slate-100" : ""}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="w-3.5 h-3.5 text-[#0055FF]" />
                    <span className="text-[9px] font-mono uppercase bg-slate-100 rounded px-1">{q.state}</span>
                    <span className="text-[10px] font-mono text-slate-400">{q.field_id}</span>
                  </div>
                  <div className="text-sm text-slate-900 line-clamp-2">{q.reason}</div>
                  <div className="text-[10px] font-mono text-slate-400 mt-1">{q.query_id} · {new Date(q.opened_at).toLocaleString()}</div>
                </button>
              ))}
              {list.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No queries in this state.</div>}
            </div>
          </div>

          <div className="lg:col-span-3">
            {detail ? (
              <div className="vq-card p-6">
                <div className="vq-overline font-mono mb-2">{detail.query_id}</div>
                <h2 className="font-display font-bold text-xl text-slate-900 mb-1">{detail.reason}</h2>
                <div className="text-[10px] font-mono text-slate-400 mb-4">
                  Entry {detail.entry_id} · Field {detail.field_id} · Opened by {detail.opened_by} on {new Date(detail.opened_at).toLocaleString()}
                </div>

                <div className="vq-overline mb-2">Responses ({(detail.responses || []).length})</div>
                <div className="space-y-2 mb-4">
                  {(detail.responses || []).map((r, i) => (
                    <div key={`resp-${i}`} className="border border-slate-200 rounded p-3 bg-slate-50">
                      <div className="text-[10px] font-mono text-slate-500">{r.by} · {new Date(r.at).toLocaleString()}</div>
                      <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{r.response}</div>
                    </div>
                  ))}
                  {(detail.responses || []).length === 0 && <div className="text-sm text-slate-500 italic">No responses yet.</div>}
                </div>

                {detail.state !== "Closed" && detail.state !== "Cancelled" && (
                  <>
                    <Textarea value={reply} data-testid="q-reply-input"
                              onChange={(e) => setReply(e.target.value)} rows={3}
                              placeholder="Response with resolution or clarification…" className="mb-2" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button onClick={doReply} data-testid="q-reply-btn" className="bg-slate-900 hover:bg-[#0055FF] text-white">
                        <Reply className="w-4 h-4 mr-2" /> Reply
                      </Button>
                      <Button variant="outline" onClick={doClose} data-testid="q-close-btn" className="border-emerald-700 text-emerald-700 hover:bg-emerald-50">
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Close query
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="vq-card p-10 text-center text-slate-500">Select a query.</div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
